import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router";
import {
  Grid,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Input,
  ListItemSecondaryAction,
} from '@mui/material';
import { TiDeleteOutline } from "react-icons/ti";
import VendorCard from "../vendors/components/VendorCard";
import VendorRow from "../vendors/components/VendorRow";
import VendorImageUploader from "../vendors/components/VendorImageUploader";
import {
  getCustomFilterValueByAttributeId, updateCustomFilterValueByAttributeId
} from "../../../actions/filters";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const EditGlobalCustomFilterDetails = () => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [filterImage, setFilterImage] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setloading] = useState(true);
  const [productAttributes, setProductAttributes] = useState([]);
  const [globalCustomFilter, setGlobalCustomFilter] = useState({});
  const { id } = useParams();
  const query = useQuery();
  const attributeId = query.get('attributeId');
  const [isBase64, setIsBase64] = useState(false)
  const [attribute, setAttribute] = useState(null)
  const [shadeOfBlue, setShadeOfBlue] = useState("");
  const [filteredProductAttributes, setFilteredProductAttributes] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Api calls for get Order
  const getAttributeDetail = useCallback(
    async (
      attributeId, id
    ) => {
      setloading(true);
      const res = await dispatch(
        getCustomFilterValueByAttributeId(attributeId, id)
      );
      if (res) {
        let details = res.globalCustomFilter?.values?.[0];
        setGlobalCustomFilter(details);
        setTitle(details?.title);
        setDescription(details?.description);
        setShadeOfBlue(details?.shadeOfBlue);
        setFilterImage(details?.image);
        setAttribute(res.attribute);
        if (details?.productAttribute?.length) {
          let selectedFilter = [];
          for (let index = 0; index < details?.productAttribute.length; index++) {
            const element = details?.productAttribute[index];
            //find from the res.productAttribute and set the selectedFilters
            let productAttribute = res.productAttribute.find((item) => item._id === element);
            if (productAttribute) {
              selectedFilter.push(productAttribute);
            }
          }
          if (selectedFilter.length) {
            setSelectedFilters(selectedFilter);
          } else {
            setSelectedFilters([]);
          }
        }
        setProductAttributes(res.productAttribute);
        setFilteredProductAttributes(res.productAttribute);

      }
      setloading(false);
    },
    [dispatch]
  );

  useEffect(() => {
    if (id) {
      var timer = setTimeout(() => {
        getAttributeDetail(attributeId, id);
      }, 1000);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [getAttributeDetail, attributeId, id]);
  // Handle selecting or deselecting colors
  const handleSelect = (color) => {
    if (selectedFilters.includes(color)) {
      setSelectedFilters(selectedFilters.filter((item) => item !== color));
    } else {
      setSelectedFilters([...selectedFilters, color]);
    }
  };

  // Handle clearing all selected attributes
  const clearAllColors = () => {
    setSelectedFilters([]);
  };
  const toBase64 = async (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    const base64 = await toBase64(file);
    setFilterImage(base64);
    setIsBase64(true);
  };

  // Handle search functionality for filtering attributes
  const handleSearch = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchTerm(searchValue);
    // Filter product attributes based on the search term
    const filteredAttributes = productAttributes.filter(attribute =>
      attribute.text.includes(searchValue)
    );
    const mergedAttributes = [...new Set([...filteredAttributes, ...selectedFilters])];
    setFilteredProductAttributes(mergedAttributes);
  };

  // Filter attributes based on search term

  const selectAllColors = () => {
    setSelectedFilters(filteredProductAttributes);
  };
  const handleSubmit = async () => {
    let productAttribute = []
    if (selectedFilters.length) {
      //get all _ids from selectedFilters and push to productAttribute
      selectedFilters.forEach((item) => {
        productAttribute.push(item._id);
      });
    }
    if (!selectedFilters?.length) {
      alert('Please select at least one attribute')
      return
    }
    let uniqueText = globalCustomFilter?.uniqueText ? globalCustomFilter?.uniqueText : Math.random().toString(36).slice(2).substr(0, 8)
    const valueData = {
      _id: id === 'create' ? null : id,
      productAttribute: productAttribute,
      showImage: isValidUrl(globalCustomFilter?.imageURL),
      attributeId: attributeId,
      title: title,
      imageURL: globalCustomFilter?.imageURL,
      description: description,
      uniqueText: uniqueText,
      image: filterImage,
      isBase64: isBase64,
    };

    await dispatch(updateCustomFilterValueByAttributeId(attributeId, valueData));
    // console.log('Value Data:', valueData,updated);
  }

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  return (
    <Grid container spacing={3} className="my-4">
      {/* Left Section (6 columns): Input Fields and Available Attributes */}
      <Grid item lg={8} md={8}>
        <VendorCard
          title={"Filter Information"}
          submitBtn={true} // Assuming you don't want a submit button here
          handleSubmit={() => handleSubmit()}
          isCreate={id === 'create' ? true : false}
        >
          <div>
            {/* Title Field */}
            <VendorRow name={"Filter Title"} editShow={true}>
              <input
                type="text"
                value={title}
                name="filterTitle"
                onChange={(e) => setTitle(e.target.value)}
              />
            </VendorRow>

            {/* Description Field */}
            <VendorRow name={"Filter Description"} editShow={true}>
              <input
                type="text"
                value={description}
                name="filterDescription"
                onChange={(e) => setDescription(e.target.value)}
              />
            </VendorRow>

            {/* Shades of Blue Field */}
            {/* <VendorRow name={"Shades of Blue"} editShow={false}>
              <input
                type="text"
                value={shadeOfBlue}
                name="shadeOfBlue"
                onChange={(e) => setShadeOfBlue(e.target.value)}
              />
            </VendorRow> */}

            {/* Image Upload */}
            <VendorImageUploader
              name={"Filter Image"}
              id={"filterImage"}
              handleChangeFile={handleImageUpload}
            >
              {filterImage || globalCustomFilter?.imageURL ? (
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "10px",
                    overflow: "hidden",
                  }}
                >
                  <img src={filterImage ? filterImage : globalCustomFilter?.imageURL} alt="Filter" className="w-100" />
                </div>
              ) : (
                <></>
              )}
            </VendorImageUploader>

            {/* Parent Attribute */}
            <VendorRow name={"Parent Attribute"} editShow={false}>
              <input type="text" value={attribute?.name} name="parentAttribute" disabled />
            </VendorRow>

            {/* Search Field */}
            <VendorRow name={"Search"} editShow={false}>
              <input
                type="text"
                value={searchTerm}
                name="search"
                placeholder="Search attributes"
                onChange={handleSearch}
              />
            </VendorRow>

            {/* Available Attributes */}
            <div style={{ maxHeight: '650px', overflowY: 'auto' }}>
              <List>
                <ListItem
                  className={`attribute-list underline }`}
                  button
                  onClick={() => (selectedFilters?.length === productAttributes?.length ? clearAllColors() : selectAllColors())}
                >
                  <Checkbox className='attribute-checkbox' checked={selectedFilters?.length === productAttributes?.length} />
                  <ListItemText primary={'Select All Attributes'} />
                </ListItem>


                {filteredProductAttributes?.map((color, index) => (
                  <ListItem className={`attribute-list ${selectedFilters.includes(color) ? 'blue-bg' : ''}`} key={index} button onClick={() => handleSelect(color)}>
                    <Checkbox className='attribute-checkbox' checked={selectedFilters.includes(color)} />
                    <ListItemText primary={color.text} />
                  </ListItem>
                ))}
              </List>
            </div>

          </div>
        </VendorCard>
      </Grid>

      {/* Right Section (6 columns): Selected Attributes */}
      <Grid item lg={4} md={4}>
        <VendorCard title={"Selected Attributes"} submitBtn={false}>
          <div style={{ maxHeight: '600px', height: '400px', overflowY: 'auto' }}>
            <List>
              {selectedFilters.length > 0 ? (
                selectedFilters.map((color, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={color.text} />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" className='attribute-checkbox' onClick={() => handleSelect(color)}>
                        <TiDeleteOutline />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))
              ) : (
                <p className='attribute-list'>No selected attributes</p>
              )}
            </List>
          </div>
          <Button fullWidth onClick={clearAllColors} className='attribute-list' variant="text">
            Clear All
          </Button>
        </VendorCard>
      </Grid>
    </Grid>
  );
};

export default EditGlobalCustomFilterDetails;
